import { Calendar, ArrowUpRight, Sparkles, Clock } from "lucide-react";

/**
 * Acuity-style scheduled programme block.
 * - Visually distinct from Stripe ticketed events:
 *     • Stripe events use the amber CTA + price chip.
 *     • Acuity (scheduled) programmes use the indigo / amber-outlined CTA
 *       and a clear "Scheduling powered by Acuity" footnote.
 * - When ACUITY_OWNER_URL is set on the backend, the booking_url query param
 *   resolves to a real Acuity link. Until then we surface the category as
 *   "Open scheduling soon" (badge) and capture interest via the linked CTA.
 *
 * Props:
 *   eyebrow, title, copy
 *   slug           — matches a backend ACUITY_BOOKING_CATEGORIES slug
 *   acuityUrl      — when provided, opens Acuity in new tab
 *   fallbackTo     — internal route for capturing interest before Acuity goes live
 *   fallbackLabel  — CTA text for the interest capture
 *   audience       — display chip ("16-25", "Adults" etc.)
 *   duration       — display chip ("45 mins", "Half-day" etc.)
 */
export default function AcuityScheduleBlock({
  eyebrow = "Scheduled programme",
  title,
  copy,
  slug,
  acuityUrl,
  fallbackTo = "/join",
  fallbackLabel = "Register interest",
  audience,
  duration,
  testId = "acuity-block",
}) {
  const isLive = Boolean(acuityUrl);
  return (
    <div
      data-testid={testId}
      data-acuity-slug={slug || undefined}
      data-acuity-live={isLive ? "1" : "0"}
      className="border border-amber-400/30 bg-zinc-950 p-6 sm:p-8"
    >
      <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold flex items-center gap-2">
        <Calendar className="h-3.5 w-3.5" />
        {eyebrow}
      </p>
      <h3 className="mt-4 font-display font-bold text-2xl leading-tight">
        {title}
      </h3>
      {copy && (
        <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{copy}</p>
      )}

      {(audience || duration) && (
        <div className="mt-5 flex flex-wrap gap-2">
          {audience && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/10 text-zinc-300">
              {audience}
            </span>
          )}
          {duration && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/10 text-zinc-300 inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> {duration}
            </span>
          )}
        </div>
      )}

      <div className="mt-6">
        {isLive ? (
          <a
            href={acuityUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-cta-live`}
            className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-6 py-3.5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
          >
            <Calendar className="h-4 w-4" /> Book a slot
            <ArrowUpRight className="h-4 w-4" />
          </a>
        ) : (
          <a
            href={fallbackTo}
            data-testid={`${testId}-cta-interest`}
            className="inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 px-6 py-3.5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
          >
            <Sparkles className="h-4 w-4" /> {fallbackLabel}
          </a>
        )}
      </div>

      <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        {isLive
          ? "Scheduling powered by Acuity"
          : "Acuity scheduling opens shortly · join the waitlist now"}
      </p>
    </div>
  );
}
